import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import CommonTable from "../CommonTable";
import { useStudentReports } from "../../hooks/useStudentReports";

import { saleTypeLabel } from "../../utils/saleHelpers";

export default function StudentDashboard({ filters, saleType = "books" }) {
  const {
    fetchSummary,
    fetchBranchGradeBreakdown,
    fetchPendingStudents,
    fetchDuplicateStudents,
  } = useStudentReports();

  const [summary, setSummary] = useState(null);
  const [breakdown, setBreakdown] = useState([]);
  const [pendingStudents, setPendingStudents] = useState([]);
  const [duplicateStudents, setDuplicateStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchSummary(filters),
      fetchBranchGradeBreakdown(filters),
      fetchPendingStudents(filters),
      fetchDuplicateStudents(filters),
    ])
      .then(([s, b, p, d]) => {
        setSummary(s);
        setBreakdown(b);
        setPendingStudents(p);
        setDuplicateStudents(d);
      })
      .finally(() => setLoading(false));
  }, [filters]);

  const gradeOrder = [
    "NURSERY",
    "LKG",
    "UKG",
    "Grade I",
    "Grade II",
    "Grade III",
    "Grade IV",
    "Grade V",
    "Grade VI",
    "Grade VII",
    "Grade VIII",
    "Grade IX",
    "Grade X",
  ];

  const chartData = useMemo(() => {
    // Group by branch
    const grouped = {};

    for (const row of breakdown) {
      if (!grouped[row.branchName]) grouped[row.branchName] = [];
      grouped[row.branchName].push(row);
    }

    // Sort grades within each branch
    const allRows = Object.entries(grouped).flatMap(([branch, items]) => {
      return [...items]
        .sort(
          (a, b) => gradeOrder.indexOf(a.grade) - gradeOrder.indexOf(b.grade)
        )
        .map((item) => ({
          label: `${branch} - ${item.grade}`,
          branch: branch,
          grade: item.grade,
          Purchased: item.purchasedStudents,
          Pending: item.pendingStudents,
        }));
    });

    return allRows;
  }, [breakdown]);

  if (loading) return <CircularProgress />;

  const coverageLabel =
    saleType === "all"
      ? "all sales"
      : `${saleTypeLabel(saleType).toLowerCase()} purchases`;

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Counts reflect <strong>{coverageLabel}</strong> for the selected branch
        and grade filters.
      </Typography>

      {/* 🔹 Summary Cards */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <StatCard label="Active Students" value={summary.activeStudents} />
        <StatCard label="Purchased" value={summary.studentsWithPurchase} />
        <StatCard
          label="Yet to Purchase"
          value={summary.studentsWithoutPurchase}
        />
        <StatCard label="Duplicates" value={summary.duplicatePurchases} />
      </Box>

      {/* 🔹 Branch-Grade Breakdown Table */}
      <Typography variant="h6" mt={4}>
        Branch → Grade Breakdown
      </Typography>
      <CommonTable
        downloadFileName="purchase_converage"
        columns={[
          { label: "Branch", field: "branchName" },
          { label: "Grade", field: "grade" },
          { label: "Active", field: "activeStudents" },
          { label: "Purchased", field: "purchasedStudents" },
          { label: "Pending", field: "pendingStudents" },
          { label: "Duplicates", field: "duplicatePurchases" },
        ]}
        rows={breakdown}
      />

      {/* 🔹 Purchase Coverage Chart */}
      <Typography variant="h6" mt={4}>
        Purchase Coverage by Grade
      </Typography>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData}>
          <XAxis
            dataKey="label"
            angle={-15}
            textAnchor="end"
            interval={0}
            height={80}
          />
          <YAxis />
          <Tooltip />
          <Legend />
          <Bar dataKey="Purchased" fill="#4caf50" />
          <Bar dataKey="Pending" fill="#f44336" />
        </BarChart>
      </ResponsiveContainer>

      {/* 🔹 Students without purchase */}
      <Typography variant="h6" mt={4}>
        Students Yet to Purchase
      </Typography>
      <CommonTable
        downloadFileName="students_to_purchase"
        columns={[
          { label: "Name", field: "name", flex: true },
          { label: "Student ID", field: "studentId" },
          { label: "Grade", field: "grade" },
          { label: "Program", field: "program" },
          {
            label: "Branch",
            renderCell: (s) => s.branch?.name || "-",
            getSearchValue: (s) => s.branch?.name || "",
          },
        ]}
        rows={pendingStudents}
      />

      {/* 🔹 Students with duplicate purchases */}
      <Typography variant="h6" mt={4}>
        Students with Multiple Purchases
      </Typography>
      <CommonTable
        downloadFileName="multiple_student_purchases"
        columns={[
          { label: "Name", field: "name", flex: true },
          { label: "Student ID", field: "studentId" },
          { label: "Grade", field: "grade" },
          { label: "Program", field: "program" },
          { label: "Purchases", field: "saleCount" },
        ]}
        rows={duplicateStudents}
      />
    </Box>
  );
}

function StatCard({ label, value }) {
  return (
    <Box p={2} borderRadius={2} bgcolor="#f1f1f1" minWidth={150}>
      <Typography variant="body2">{label}</Typography>
      <Typography variant="h6">{value}</Typography>
    </Box>
  );
}
